import { Box, CardContent, Divider, Stack } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { ResourceGroupSelect } from '@/components/selectors/resourceGroupSelect';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import { DiskSettingsSection } from '@/components/volumes/azureDiskSettings';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreAzureDiskState } from '../restoreAzureDiskWizard';
export const AzureDiskConfiguration = (
  props: StepProps<RestoreAzureDiskState>
) => {
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const dal = useDAL();
  const diskSettings = props.wizardState.keepOriginalSettings
    ? props.wizardState.initialDiskSettings
    : props.wizardState.diskSettings;

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);
    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };

    return dal.restore.azureVm
      .restoreDisk(
        props.wizardState.resourceId,
        props.wizardState.snapshot.id,
        {
          restoreAccountId: props.wizardState.restoreAccount!.id,
          providerDiskId: diskSettings.providerDiskId,
          destination: {
            azureDisk: {
              resourceGroupName: props.wizardState.resourceGroup!,
              region: props.wizardState.location!,
              settings: {
                name: diskSettings.settings.name,
                tier: diskSettings.settings.tier,
                type: diskSettings.settings.type,
                sizeBytes: diskSettings.settings.sizeBytes,
                hyperVGeneration: diskSettings.settings.hyperVGeneration,
                tags,
              },
            },
          },
        }
      )
      .then(() =>
        props.finish(`Started restore job for ${diskSettings.settings.name}`)
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore disk: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={() =>
        !isRestoring &&
        !!diskSettings?.providerDiskId &&
        !!diskSettings?.settings.name &&
        !!props.wizardState.location &&
        !!props.wizardState.resourceGroup &&
        !!diskSettings.settings.sizeBytes
      }
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <RestoreRegionSelect
            isRequired
            restoreAccountId={props.wizardState.restoreAccount?.id}
            cloudProvider={Provider.Azure}
            regionName={props.wizardState.location}
            onChange={(location) =>
              props.setWizardState((state) => ({ ...state, location }))
            }
          />
          <ResourceGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            onChange={(resourceGroup) =>
              props.setWizardState((state) => ({
                ...state,
                resourceGroup,
              }))
            }
          />
        </Stack>
      </Box>
      <Divider />
      <CardContent className='p-0'>
        <DiskSettingsSection
          initialSettings={props.wizardState.initialDiskSettings}
          settings={props.wizardState.diskSettings}
          accountId={props.wizardState.restoreAccount?.id ?? ''}
          location={props.wizardState.location ?? ''}
          keepOriginalSettings={props.wizardState.keepOriginalSettings}
          onChange={(diskSettings) =>
            props.setWizardState((state) => ({ ...state, diskSettings }))
          }
          setKeepOriginalSettings={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalSettings: value,
            }))
          }
        />
      </CardContent>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags ?? {}}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(keepOriginalTags) =>
            props.setWizardState((state) => ({ ...state, keepOriginalTags }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
